import { useEffect, useLayoutEffect, useRef, useState } from "react";
import styled, { useTheme, css } from "styled-components";
import Icon from "../../../../components/Icon/Icon";
import { GalleryBody, StyledGalleryCard } from "../../../Judging/GalleryCard";
import {
	GalleryContainer,
	StyledVerticalDivider,
	splitBatchLabels,
} from "../../../Judging/JudgingGallery";
import { ActiveBar } from "../../../Judging/JudgingGalleryNav";
import { getThumbnailSrc } from "../../../MediaLibrary/manageMediaLibrary";

import Loading from "../../../../components/Loading/Loading";

import StickyCard, {
	StickyCardBody,
} from "../../../../components/StickyCard/StickyCard";
import Collapsible from "../../../../components/Collapsible/Collapsible";
import {
	GalleryBatchCard,
	GalleryEntryCard,
	LiveToolsSettings,
} from "../JudgingInterfaces";
import AwardSummaryBox, {
	calculateEntryAwardIndicator,
	calculateEntryResultIndicator,
} from "./AwardSummaryBox";
import filterEntries from "./EntryFilter";
import { StyledTitleSlideScoreIndicator } from "./MediaViewer";
import {
	voteButtonColor,
	getContrastingTextColor,
	isNearBlackColor,
} from "../../../Judging/GalleryCard";
import replaceSingleEntry, {
	filterBatchLabels,
} from "../../../Judging/replaceSingleEntry";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import {
	areAllVotesIn,
	getRoundBehavior,
	getScoreboardDisplayFlags,
	getScoreboardProfile,
	hasSubmittedVote,
	isGrandOrWinnerRound,
	ScoreboardProfile,
	usesEntryResultDisplayMode as roundUsesEntryResultDisplayMode,
	isScoreRound,
} from "../judgingRoundUtils";

const gridCardWidth = 430;
// Worst case content (3-line brand + 3-line title + 5-line hierarchy + one row of award boxes) is ~392px.
const scoreboardCardMinHeight = 370;
const scoreboardCardMaxHeight = 392;
const getSettingsRoundBehaviorType = (settings: LiveToolsSettings) =>
	getRoundBehavior(
		settings.roundStructure?.name,
		settings.roundStructure?.roundType?.behaviorType,
	);

const EntryContainer = styled(GalleryContainer)<{
	viewType: ViewType;
	scoreboardView?: boolean;
}>`
	margin-bottom: 365px;
	padding: 0 1rem;

	${(p) =>
		p.viewType === ViewType.List
			? `
          display: flex;
          flex-direction: column;
          gap: 1rem;  
        `
			: p.scoreboardView
				? `
          @media only screen and (min-width: ${p.theme.sm}) {
            display: grid;
            grid-template-columns: repeat(auto-fill, ${gridCardWidth + "px"});
            grid-auto-rows: 1fr;
          }
        `
				: `
          @media only screen and (min-width: ${p.theme.sm}) {
            display: grid;
            grid-template-columns: repeat(auto-fill, 250px);
          }
        `}
`;

const StyledEntryGridCard = styled(StyledGalleryCard)<{
	highlighted?: boolean;
	scoreboardView?: boolean;
	isLocked?: boolean;
}>`
	display: flex;
	min-height: 275px;
	max-width: 250px;
	border-bottom: 2px solid ${({ theme }) => theme.colorBorderLight};
	cursor: pointer;

	@media only screen and (min-width: ${({ theme }) => theme.sm}) {
		min-height: 300px;
	}

	${(p) =>
		p.scoreboardView &&
		css`
			border-bottom: 0;
			max-width: ${gridCardWidth + "px"};
			min-height: ${scoreboardCardMinHeight + "px"} !important;
			max-height: ${scoreboardCardMaxHeight + "px"};
			${ActiveBar} {
				background: transparent;
			}

			.award-container {
				flex-shrink: 0;
				display: flex;
				width: 100%;
				flex-wrap: wrap;
				gap: 0.25rem;
				padding: 1rem;
				background: ${p.theme.colorBackgroundDarkDark};
				color: ${p.theme.colorCopyLightLight};
			}

			.cover-img {
				width: 216px;
				height: 100%;
				object-fit: cover;
				aspect-ratio: 1 / 1;
				overflow-clip-margin: unset;
			}
		`};

	${ActiveBar} {
		bottom: -2px;
	}

	&:hover {
		${GalleryBody} {
			filter: none;
		}

		${ActiveBar} {
			width: 100%;
		}
	}

	${(p) =>
		p.highlighted &&
		`
      ${ActiveBar} {
        background: ${p.theme.colorPrimary};
        width: 100%;
      }
    `}

	${(p) =>
		p.isLocked &&
		`
      filter: brightness(85%);
    `}
`;

const StyledEntryListCard = styled.div<{
	highlighted?: boolean;
	scoreboardView?: boolean;
	isLocked?: boolean;
}>`
	position: relative;
	display: flex;
	flex-wrap: wrap;
	align-items: stretch;
	gap: 2rem;
	/* padding: 1rem 1.5rem; */
	background: ${({ theme }) => theme.colorBackgroundLightLight};
	box-shadow: 0 2px 4px 0 ${({ theme }) => theme.colorBoxShadow};
	cursor: pointer;

	${(p) =>
		p.scoreboardView &&
		css`
			.award-container {
				display: flex;
				align-items: center;
				flex-wrap: wrap;
				gap: 0.25rem;
				padding: 1rem;
				background: ${p.theme.colorBackgroundDarkDark};
				color: ${p.theme.colorCopyLightLight};
			}

			.list-view-img {
				object-fit: cover;
				aspect-ratio: 1 / 1;
				overflow-clip-margin: unset;
			}

			@media only screen and (min-width: 1600px) {
				.award-container:not(.single-result) {
					display: flex;
					justify-content: center;
					min-width: 400px;
				}

				h2 {
					font-size: 2rem !important;
				}
				p {
					font-size: 1.25rem !important;
					line-height: 27px;
				}

				.list-view-img {
					flex: 0 0 300px;
					height: 169px;
				}
			}
		`};

	${ActiveBar} {
		bottom: -2px;
	}

	&:hover {
		${ActiveBar} {
			width: 100%;
		}
	}

	img {
		width: 178px;
		height: 100px;
	}

	${(p) =>
		p.highlighted &&
		`
      ${ActiveBar} {
        background: ${p.theme.colorPrimary};
        width: 100%;
      }
    `}

	${(p) =>
		p.isLocked &&
		`
      filter: brightness(85%);
    `}
`;

const PhysicalComponentFlag = styled.div`
	position: absolute;
	top: 0;
	right: 0;
	z-index: 10;
	width: 45px;
	height: 45px;
	border-left: 45px solid transparent;
	border-top: 45px solid ${({ theme }) => theme.colorBackgroundDarkDark};

	.cube {
		position: absolute;
		top: -42px;
		right: 3px;
	}
`;

function getResultColor(label: string, theme: any): string | undefined {
	const num = parseFloat(label);
	if (!isNaN(num)) {
		return voteButtonColor(String(Math.round(num)), theme);
	}
	return voteButtonColor(label, theme);
}

function usesEntryResultDisplayMode(settings: LiveToolsSettings): boolean {
	return roundUsesEntryResultDisplayMode(
		settings.roundStructure?.name,
		settings.roundStructure?.roundType?.behaviorType,
		settings.roundStructure?.roundType?.voteOptions,
	);
}

function getScoreboardBadgeLabel(
	roundEntry: GalleryEntryCard,
	settings: LiveToolsSettings,
	allVotesIn: boolean,
	scoreboardView?: boolean,
): string | undefined {
	const isEntryResultScoreboardCard =
		scoreboardView && usesEntryResultDisplayMode(settings);

	if (!allVotesIn) {
		return undefined;
	}

	if (isEntryResultScoreboardCard) {
		if (!settings.showVoteCounts) {
			return "Voted";
		}

		const voteOptions = settings.roundStructure?.roundType?.voteOptions || [];
		return calculateEntryResultIndicator(
			roundEntry,
			settings.roundStructure?.name || "",
			settings.roundStructure?.roundType?.behaviorType,
			voteOptions,
		);
	}

	if (!scoreboardView) {
		return calculateEntryAwardIndicator(
			roundEntry,
			settings.roundStructure?.name || "",
			settings.roundStructure?.roundType?.behaviorType,
			settings.roundStructure?.roundType?.voteOptions || [],
			false,
			false,
		);
	}

	return undefined;
}

const ResultBadge = styled.div<{ background: string }>`
	position: absolute;
	top: 0.5rem;
	left: 0.5rem;
	z-index: 10;
	display: flex;
	align-items: center;
	justify-content: center;
	min-width: 51px;
	padding: 0.375rem 0.75rem;
	border-radius: 4px;
	background: ${(p) => p.background};
	color: ${(p) => getContrastingTextColor(p.background, p.theme)};
	font-size: ${({ theme }) => theme.xSmallSize};
	font-weight: 700;
	line-height: 1;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
	${(p) =>
		isNearBlackColor(p.background) &&
		`
			border: 1px solid ${p.theme.colorCopyLightLight};
		`}
`;

export function countExplicitNoGrandJurors(
	galleryEntryCards: GalleryEntryCard[],
): number | undefined {
	let noGrandJurors = new Set<number>();
	let grandOrAbstainJurors = new Set<number>();

	for (let card of galleryEntryCards) {
		for (let vote of card.votes) {
			if (!vote.voteOption) {
				// If any voteOption is undefined, return undefined
				return undefined;
			} else if (vote.voteOption?.value === 0) {
				noGrandJurors.add(vote.jurorId);
			} else if (
				vote.voteOption?.value === 1 ||
				vote.voteOption?.value === null
			) {
				grandOrAbstainJurors.add(vote.jurorId);
			}
		}
	}

	// Find jurors who voted "No Grand" but did not vote "Grand" or "Abstain"
	let explicitNoGrandJurors = [...noGrandJurors].filter(
		(jurorId) => !grandOrAbstainJurors.has(jurorId),
	);

	return explicitNoGrandJurors.length;
}

export function IsNoGrandMajority(
	galleryEntryCards: GalleryEntryCard[],
): boolean {
	for (let card of galleryEntryCards) {
		if (card.score! > 0 || !card.votes.every((x) => x.voteOptionId)) {
			return false;
		}
	}
	return true;
}

const TITLE_MIN_LINES = 3;
const TITLE_MAX_LINES = 5;

const EntryGridCard = (props: EntryCard) => {
	const theme = useTheme();

	// The h2 sits in normal flow clamped to 3 lines, so the card only reserves the space the
	// title needs (a 1-line title takes 1 line). The box around it is flex-1 and absorbs any
	// slack the grid row gives the card; the clamp then grows into that slack, up to 5 lines.
	const titleBoxRef = useRef<HTMLDivElement>(null);
	const [titleClampLines, setTitleClampLines] = useState(TITLE_MIN_LINES);

	useLayoutEffect(() => {
		const box = titleBoxRef.current;
		if (!box) return;
		const measure = () => {
			const heading = box.firstElementChild as HTMLElement | null;
			if (!heading) return;
			const lineHeight = parseFloat(getComputedStyle(heading).lineHeight);
			if (!lineHeight) return;
			// Measure the slack with the clamp reset to the minimum, so a previously expanded
			// title can't keep the box (and the row) taller than it needs to be.
			const previous = heading.style.getPropertyValue("-webkit-line-clamp");
			heading.style.setProperty("-webkit-line-clamp", String(TITLE_MIN_LINES));
			const boxHeight = box.clientHeight;
			heading.style.setProperty("-webkit-line-clamp", previous);
			const fits = Math.floor((boxHeight + 0.5) / lineHeight);
			setTitleClampLines(
				Math.min(TITLE_MAX_LINES, Math.max(TITLE_MIN_LINES, fits)),
			);
		};
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(box);
		return () => observer.disconnect();
	}, []);
	const allVotesIn = props.votes.every((vote) => hasSubmittedVote(vote));
	const scoreboardProfile = getScoreboardProfile(
		props.settings.roundStructure?.name,
		props.settings.roundStructure?.roundType?.behaviorType,
		props.settings.roundStructure?.roundType?.voteOptions,
	);
	const displayFlags = getScoreboardDisplayFlags(
		scoreboardProfile,
		props.settings.showVoteCounts,
		props.settings.revealWinner,
	);
	const isEntryResultScoreboardCard =
		props.scoreboardView && scoreboardProfile === ScoreboardProfile.Echo;
	const isWinnerRevealRound = isGrandOrWinnerRound(
		props.settings.roundStructure?.name,
		props.settings.roundStructure?.roundType?.behaviorType,
	);
	const canShowScoreboardVoteBox = displayFlags.showCardVoteBoxes;
	const canRevealCardCounts =
		displayFlags.revealCardCounts &&
		(!displayFlags.cardCountsRequireAllVotes ||
			(props.allRoundVotesIn ?? allVotesIn));
	const resultLabel = getScoreboardBadgeLabel(
		props,
		props.settings,
		allVotesIn,
		props.scoreboardView,
	);
	const resultColor = resultLabel
		? getResultColor(resultLabel, theme)
		: undefined;

	return (
		<StyledEntryGridCard
			id={String(props.id)}
			tint={false}
			onClick={() => props.onClick()}
			highlighted={props.highlighted}
			scoreboardView={props.scoreboardView}
			isLocked={props.isLocked}
		>
			{!isEntryResultScoreboardCard && resultColor && resultLabel && (
				<ResultBadge background={resultColor}>{resultLabel}</ResultBadge>
			)}
			{props.hasPhysicalComponent && (
				<PhysicalComponentFlag>
					<Icon
						className="cube"
						icon="cube"
						color={theme.colorPrimary}
						width="18px"
						height="18px"
					/>
				</PhysicalComponentFlag>
			)}
			<div
				className={
					props.scoreboardView ? "flex flex-auto" : "flex flex-col h-full"
				}
			>
				<img
					className="cover-img"
					src={(props.coverImage && getThumbnailSrc(props.coverImage!)) || ""}
					alt="Cover Image"
				/>

				{props.isLocked && (
					<Icon
						className="absolute bottom-[.5rem] right-[.5rem] z-[10]"
						icon="lock"
						color={
							props.scoreboardView
								? theme.colorCopyLightLight
								: theme.colorPrimary
						}
						width="20px"
						height="20px"
					/>
				)}
				<GalleryBody className="relative !border-0 !p-[1.5rem_1rem] !pb-[2rem]">
					<div className="section flex flex-col flex-1">
						<p className="subtext mb-[.25rem] break-normal leading-[1.3] line-clamp-3">
							{props.brand}
						</p>
						<div ref={titleBoxRef} className="flex-1">
							<h2
								className="playfair-500 text-[1.25rem] leading-[1.3] overflow-hidden"
								style={{
									display: "-webkit-box",
									WebkitBoxOrient: "vertical",
									WebkitLineClamp: titleClampLines,
								}}
							>
								{props.title}
							</h2>
						</div>
					</div>
					<p className="absolute right-[.5rem] bottom-[.5rem] text-xxxSmallSize text-colorCopyLight">
						{props.entryId}
					</p>

					<div className="text-xxSmallSize leading-[1.25rem] text-colorCopyLight mt-auto text-right line-clamp-5">
						{props.scoreboardView &&
							props.batchHierarchyDefinitions &&
							props.batchHierarchyDefinitions.length > 1 &&
							props.batchHierarchyDefinitions.map(
								(level, i) =>
									i !== 0 && (
										<>
											<span className="whitespace-break-spaces text-xxSmallSize leading-[1.25rem]">
												{level.name === "Single Entry" ? "Single" : level.name}
											</span>
											{i !== props.batchHierarchyDefinitions!.length - 1
												? " | "
												: ""}
										</>
									),
							)}
					</div>
				</GalleryBody>
				{!props.scoreboardView && (
					<div className="text-xxxSmallSize text-colorCopyLight p-[.5rem]">
						<p
							className="whitespace-pre-wrap"
							dangerouslySetInnerHTML={{
								__html: props.companyName,
							}}
						></p>
					</div>
				)}
			</div>

			{props.scoreboardView && canShowScoreboardVoteBox && (
				// for the grand/winner round, the vote results only show if revealWinner is true and all votes have been cast
				// otherwise vote results show when all votes have been cast for the entry
				<div
					className={`award-container${
						displayFlags.singleResultOnly ? " single-result" : ""
					}`}
				>
					<AwardSummaryBox
						roundEntry={props}
						roundType={props.settings.roundStructure?.name || ""}
						roundBehaviorType={getSettingsRoundBehaviorType(props.settings)}
						voteOptions={
							props.settings.roundStructure?.roundType?.voteOptions || []
						}
						awardOnly={isWinnerRevealRound || false}
						includeAbstain={displayFlags.includeAbstainOnCards}
						revealResults={canRevealCardCounts}
						votedOnly={displayFlags.cardVotedOnly}
						includeUnvoted={false}
						scoreAndAbstainOnly={isScoreRound(
							props.settings.roundStructure?.name,
							props.settings.roundStructure?.roundType?.behaviorType,
						)}
						entryResultMode={isEntryResultScoreboardCard}
						singleResultOnly={displayFlags.singleResultOnly}
					/>
				</div>
			)}

			<ActiveBar leftPosition="0" />
		</StyledEntryGridCard>
	);
};

const EntryListCard = (props: EntryCard) => {
	const theme = useTheme();
	const { labels } = useSelector((state: RootState) => state.labels);
	const allVotesIn = props.votes.every((vote) => hasSubmittedVote(vote));
	const scoreboardProfile = getScoreboardProfile(
		props.settings.roundStructure?.name,
		props.settings.roundStructure?.roundType?.behaviorType,
		props.settings.roundStructure?.roundType?.voteOptions,
	);
	const displayFlags = getScoreboardDisplayFlags(
		scoreboardProfile,
		props.settings.showVoteCounts,
		props.settings.revealWinner,
	);
	const isEntryResultScoreboardCard =
		props.scoreboardView && scoreboardProfile === ScoreboardProfile.Echo;
	const isWinnerRevealRound = isGrandOrWinnerRound(
		props.settings.roundStructure?.name,
		props.settings.roundStructure?.roundType?.behaviorType,
	);
	const canShowScoreboardVoteBox = displayFlags.showCardVoteBoxes;
	const canRevealCardCounts =
		displayFlags.revealCardCounts &&
		(!displayFlags.cardCountsRequireAllVotes ||
			(props.allRoundVotesIn ?? allVotesIn));
	const resultLabel = getScoreboardBadgeLabel(
		props,
		props.settings,
		allVotesIn,
		props.scoreboardView,
	);
	const resultColor = resultLabel
		? getResultColor(resultLabel, theme)
		: undefined;

	return (
		<StyledEntryListCard
			id={String(props.id)}
			onClick={() => props.onClick()}
			highlighted={props.highlighted}
			scoreboardView={props.scoreboardView}
			isLocked={props.isLocked}
		>
			{!isEntryResultScoreboardCard && resultColor && resultLabel && (
				<ResultBadge background={resultColor}>{resultLabel}</ResultBadge>
			)}
			{props.isLocked && (
				<Icon
					className="absolute bottom-[.5rem] right-[.5rem] z-[10]"
					icon="lock"
					color={theme.colorPrimary}
					width="20px"
					height="20px"
				/>
			)}

			{props.hasPhysicalComponent && (
				<PhysicalComponentFlag>
					<Icon
						className="cube"
						icon="cube"
						color={theme.colorPrimary}
						width="18px"
						height="18px"
					/>
				</PhysicalComponentFlag>
			)}

			<div className="flex items-center p-[1rem_1.5rem] flex-1 gap-[1rem]">
				<img
					className="list-view-img"
					src={(props.coverImage && getThumbnailSrc(props.coverImage!)) || ""}
					alt="Cover Image"
				/>
				<div className="flex flex-col lg:w-[300px] xxl:w-[400px]">
					<p className="subtext mb-[.25rem]">{props.brand}</p>
					<h2 className="playfair-500 text-[1.25rem]">{props.title}</h2>
					<p className="mt-auto text-xxSmallSize text-colorCopyLight">
						{props.entryId}
					</p>
				</div>
				<div className="flex flex-col">
					{props.batchHierarchyDefinitions?.flatMap((level, i) =>
						i !== 0 ? (
							<p
								key={`entry.${props.entryId}.hierarchyLabel.${i}`}
								className="text-xxSmallSize"
							>{`${level.label} : ${replaceSingleEntry(
								labels,
								level.name,
							)}`}</p>
						) : (
							[]
						),
					)}
				</div>
				{!props.scoreboardView && (
					<div className="text-right flex flex-col ml-auto">
						<p
							className="text-xxSmallSize whitespace-pre-wrap"
							dangerouslySetInnerHTML={{
								__html: props.companyName,
							}}
						></p>
					</div>
				)}
			</div>

			{props.scoreboardView && canShowScoreboardVoteBox && (
				// for the grand/winner round, the vote results only show if revealWinner is true and all votes have been cast
				// otherwise vote results show when all votes have been cast for the entry
				<div
					className={`award-container${
						displayFlags.singleResultOnly ? " single-result" : ""
					}`}
				>
					<AwardSummaryBox
						roundEntry={props}
						roundType={props.settings.roundStructure?.name || ""}
						roundBehaviorType={getSettingsRoundBehaviorType(props.settings)}
						voteOptions={
							props.settings.roundStructure?.roundType?.voteOptions || []
						}
						awardOnly={isWinnerRevealRound || false}
						includeAbstain={displayFlags.includeAbstainOnCards}
						revealResults={canRevealCardCounts}
						votedOnly={displayFlags.cardVotedOnly}
						includeUnvoted={false}
						scoreAndAbstainOnly={isScoreRound(
							props.settings.roundStructure?.name,
							props.settings.roundStructure?.roundType?.behaviorType,
						)}
						entryResultMode={isEntryResultScoreboardCard}
						singleResultOnly={displayFlags.singleResultOnly}
						large
					/>
				</div>
			)}
			<ActiveBar leftPosition="0" />
		</StyledEntryListCard>
	);
};

const SmallGalleryContainer = styled(GalleryContainer)<{
	viewType: ViewType;
	width: string;
}>`
	${(p) =>
		p.viewType === ViewType.List
			? `
         display: flex;
`
			: `
  @media only screen and (min-width: ${p.theme.sm}) {
    display: grid;
    grid-template-columns: repeat(auto-fill, ${p.width});
  }
`}
`;

export const BatchContainer = (props: {
	id: number;
	showBatchDivider: boolean;
	children: React.ReactNode;
	title: string;
	entryCount: number;
	viewType: ViewType;
	cardWidth: string;
	background?: string;
}) => {
	const theme = useTheme();
	const { labels } = useSelector((state: RootState) => state.labels);
	const { settings: appSettings } = useSelector(
		(state: RootState) => state.settings,
	);
	const hideSingleCampaignBatchLabel =
		!!appSettings.featureFlags?.["Hide Single/Campaign Batch Label"];
	const [isCollapsed, setIsCollapsed] = useState(false);
	const batchLabels = filterBatchLabels(
		splitBatchLabels(props.title),
		hideSingleCampaignBatchLabel,
		labels.entry.singular,
	);

	return props.showBatchDivider ? (
		<StickyCard
			className={`!relative !top-0 !max-h-fit !min-h-0`}
			titleElement={
				<div className="flex items-center flex-wrap gap-[.5rem]">
					{batchLabels.map((label, i) => (
						<>
							<h2
								key={`batch.${String(props.id)}.label.${i}`}
								className="whitespace-nowrap text-xSmallSize xl:text-pSize xxl:text-h2Size"
							>
								{replaceSingleEntry(labels, label)}
							</h2>
							{i !== batchLabels.length - 1 && (
								<StyledVerticalDivider
									key={`batch.${String(props.id)}.divider.${i}`}
								/>
							)}
						</>
					))}
				</div>
			}
			rightTitle={`${props.entryCount} Entries`}
			headerIcons={[
				<Icon
					icon="caret"
					color={theme.colorCopyLightLight}
					height="21px"
					width="21px"
					className="caret"
					rotation={isCollapsed ? "180deg" : "0"}
					onClick={() => setIsCollapsed(!isCollapsed)}
				/>,
			]}
			background={props.background}
		>
			<Collapsible isCollapsed={isCollapsed}>
				<StickyCardBody className="flex flex-col gap-[1.875rem] !p-[.75rem_1rem] sm:!p-[2.5rem_1.5rem]">
					<SmallGalleryContainer
						viewType={props.viewType}
						width={props.cardWidth}
					>
						{props.children}
					</SmallGalleryContainer>
				</StickyCardBody>
			</Collapsible>
		</StickyCard>
	) : (
		<>{props.children}</>
	);
};

const EntryViewContainer = (props: {
	showBatchDivider: boolean;
	children: React.ReactNode;
	viewType: ViewType;
}) => {
	if (props.showBatchDivider) {
		return (
			<div className="flex flex-col gap-[2rem] p-[0_2.5rem]">
				{props.children}
			</div>
		);
	} else
		return (
			<EntryContainer key={props.viewType} viewType={props.viewType}>
				{props.children}
			</EntryContainer>
		);
};

/* for the grand round in the scoreboard, show a tally of "no
              grand" and "abstain" votes */
const ScoreCount = (props: {
	className?: string;
	id: string;
	count?: number;
	voteName: string;
	highlighted: boolean;
	background: string;
}) => {
	return (
		<StyledEntryGridCard
			className={`${
				props.className ? props.className : ""
			} !bg-colorBackgroundDarkDark !max-w-full p-[2rem]`}
			id={props.id}
			tint={false}
			scoreboardView={true}
		>
			<div className="flex justify-center items-center h-full w-full">
				<StyledTitleSlideScoreIndicator
					className="flex-col gap-[.5rem]"
					background={props.background}
				>
					<p className="text-[1.5rem] font-bold">{props.count || "-"}</p>
					<p>{props.voteName}</p>
				</StyledTitleSlideScoreIndicator>
			</div>
		</StyledEntryGridCard>
	);
};

function checkDiff(a: any[]) {
	return new Set(a).size !== 1;
}

const EntryView = (props: EntryViewProps) => {
	const theme = useTheme();
	const roundBehaviorType = getSettingsRoundBehaviorType(props.settings);
	const scoreboardProfile = getScoreboardProfile(
		props.settings.roundStructure?.name,
		props.settings.roundStructure?.roundType?.behaviorType,
		props.settings.roundStructure?.roundType?.voteOptions,
	);
	const displayFlags = getScoreboardDisplayFlags(
		scoreboardProfile,
		props.settings.showVoteCounts,
		props.settings.revealWinner,
	);
	// Measured across the whole round so cards and totals reveal together.
	const allRoundVotesIn = areAllVotesIn(props.roundEntries);
	const [filteredBatches, setFilteredBatches] = useState<GalleryBatchCard[]>(
		props.batches,
	);
	const [filteredEntries, setFilteredEntries] = useState<GalleryEntryCard[]>(
		props.roundEntries,
	);

	// obj: if batch division is enabled
	const objEntries =
		filteredBatches && filteredBatches.length > 0
			? filteredBatches.map((batch) => {
					return (
						<BatchContainer
							key={batch.id}
							id={batch.id}
							showBatchDivider={props.settings.includeBatchDivisions}
							title={batch.label}
							entryCount={batch.roundEntries.length}
							viewType={props.settings.displayConfiguration}
							cardWidth={props.scoreboardView ? gridCardWidth + "px" : "250px"}
						>
							{batch.roundEntries.map((roundEntryCard) => {
								return props.settings.displayConfiguration === ViewType.List ? (
									<EntryListCard
										key={roundEntryCard.id}
										onClick={() =>
											props.onClickCard && props.onClickCard(roundEntryCard)
										}
										{...roundEntryCard}
										highlighted={
											roundEntryCard.id ===
											props.settings.highlightedRoundEntryId
										}
										settings={props.settings}
										scoreboardView={props.scoreboardView}
										allRoundVotesIn={allRoundVotesIn}
									/>
								) : (
									<EntryGridCard
										key={roundEntryCard.id}
										onClick={() =>
											props.onClickCard && props.onClickCard(roundEntryCard)
										}
										{...roundEntryCard}
										highlighted={
											roundEntryCard.id ===
											props.settings.highlightedRoundEntryId
										}
										settings={props.settings}
										scoreboardView={props.scoreboardView}
										allRoundVotesIn={allRoundVotesIn}
									/>
								);
							})}
						</BatchContainer>
					);
				})
			: null;

	// arr: if batch division is disabled
	const arrEntries =
		filteredEntries && filteredEntries.length > 0
			? filteredEntries.map((roundEntryCard) => {
					return props.settings.displayConfiguration === ViewType.List ? (
						<EntryListCard
							key={roundEntryCard.id}
							onClick={() =>
								props.onClickCard && props.onClickCard(roundEntryCard)
							}
							{...roundEntryCard}
							highlighted={
								roundEntryCard.id === props.settings.highlightedRoundEntryId
							}
							settings={props.settings}
							scoreboardView={props.scoreboardView}
							allRoundVotesIn={allRoundVotesIn}
						/>
					) : (
						<EntryGridCard
							key={roundEntryCard.id}
							onClick={() =>
								props.onClickCard && props.onClickCard(roundEntryCard)
							}
							{...roundEntryCard}
							highlighted={
								roundEntryCard.id === props.settings.highlightedRoundEntryId
							}
							settings={props.settings}
							scoreboardView={props.scoreboardView}
							allRoundVotesIn={allRoundVotesIn}
						/>
					);
				})
			: null;

	useEffect(() => {
		if (props.settings.includeBatchDivisions && props.batches.length > 0) {
			// filter batches
			const filteredBatches = filterEntries(
				props.searchVal || "",
				props.batches,
				undefined,
			);
			setFilteredBatches(filteredBatches as GalleryBatchCard[]);
		} else if (
			!props.settings.includeBatchDivisions &&
			props.roundEntries.length > 0
		) {
			// filter round entries (no batch divisions)
			const filteredEntries = filterEntries(
				props.searchVal || "",
				undefined,
				props.roundEntries,
			);

			setFilteredEntries(filteredEntries as GalleryEntryCard[]);
		}
	}, [props.batches, props.roundEntries, props.searchVal]);

	const isNoGrandMajority =
		filteredEntries &&
		filteredEntries.length > 0 &&
		IsNoGrandMajority(filteredEntries);

	return props.roundEntries.length > 0 || props.batches.length > 0 ? (
		props.settings.includeBatchDivisions ? (
			<div className="flex flex-col gap-[2rem] p-[0_2.5rem] mb-[300px]">
				{objEntries}
			</div>
		) : (
			<EntryContainer
				key={props.settings.displayConfiguration}
				viewType={props.settings.displayConfiguration}
				scoreboardView={props.scoreboardView}
			>
				{arrEntries}
				{/* for the grand round, show 1 "no grand"/abstain card per entry set */}
				{props.scoreboardView &&
					// check if multiple entry sets are selected
					!checkDiff(
						props.settings.selectedBatches.map((batch) => batch.entrySetId),
					) &&
					filteredEntries &&
					filteredEntries.length > 0 && (
						<>
							{roundBehaviorType === "Grand" &&
								displayFlags.showNoGrandScoreCard && (
									<ScoreCount
										id={`entrySet.${filteredEntries[0].entrySetId}.noGrand`}
										className={
											props.settings.displayConfiguration === ViewType.List
												? "h-[201px]"
												: ""
										}
										count={
											props.settings.showVoteCounts &&
											props.settings.revealWinner &&
											allRoundVotesIn
												? countExplicitNoGrandJurors(filteredEntries)
												: undefined
										}
										voteName="No Grand"
										highlighted={
											props.settings.revealWinner && isNoGrandMajority
										}
										background={
											props.settings.revealWinner && isNoGrandMajority
												? voteButtonColor("No Grand", theme)
												: "transparent"
										}
									/>
								)}
							{(roundBehaviorType === "Grand" ||
								roundBehaviorType === "Winner") &&
								displayFlags.showAbstainScoreCard && (
									<ScoreCount
										id={`entrySet.${filteredEntries[0].entrySetId}.abstain`}
										className={
											props.settings.displayConfiguration === ViewType.List
												? "h-[201px]"
												: ""
										}
										count={
											props.settings.revealWinner && allRoundVotesIn
												? filteredEntries[0].votes.reduce(
														(count, vote) =>
															vote.voteOption && vote.voteOption.value === null
																? count + 1
																: count,
														0,
													)
												: undefined
										}
										voteName="Abstain"
										highlighted={false}
										background="transparent"
									/>
								)}
						</>
					)}
			</EntryContainer>
		)
	) : (
		<Loading />
	);
};

export default EntryView;

interface EntryViewProps {
	settings: LiveToolsSettings;
	batches: Array<GalleryBatchCard>;
	roundEntries: Array<GalleryEntryCard>;
	onClickCard?: (roundEntry: GalleryEntryCard) => void;
	updateHighlightedRoundEntryId?: (roundEntryId?: number) => void;
	searchVal?: string;
	scoreboardView?: boolean;
}

export enum ViewType {
	List,
	Grid,
}

export enum EntrySortOptions {
	PreviousRoundScoreDescending,
	PreviousRoundScoreAscending,
	CurrentScoreDescending,
	CurrentScoreAscending,
	EntryBrandDescending,
	EntryBrandAscending,
	EntryTitleDescending,
	EntryTitleAscending,
}

interface EntryCard extends GalleryEntryCard {
	onClick: () => void;
	highlighted: boolean;
	settings: LiveToolsSettings;
	scoreboardView?: boolean;
	allRoundVotesIn?: boolean;
}
